import * as THREE from "three";

/**
 * GPU-based Poison/Effluvium Effect
 * All animation is done in the vertex shader - no CPU updates per frame
 */
export default class PoisonEffect {
  BASE_PARTICLE_COUNT = 1000;
  PARTICLE_SIZE = 0.2;

  particleSystem = undefined;
  startingLowerLimit = 0;  // Keep particles above floor level
  upperLimit = 1.2;  // Poison particles stay lower in the room

  group = new THREE.Group();

  constructor(intensity) {
    intensity = Math.min(Math.max(intensity, 0), 100);

    window.floorSize = new THREE.Vector3();
    window.floor.geometry.computeBoundingBox();
    window.floor.geometry.boundingBox.getSize(floorSize);

    this.setup(intensity);
  }

  setup(intensity) {
    if (intensity == this.currentIntensity) return;

    // Since the intensity has changed, we have to clean up
    let parent = this.group.parent;
    if (parent) {
      window.gu.time.value = 0;
      this.group.parent.remove(this.group);
      this.group = new THREE.Group();
      parent.add(this.group);
    }

    // Apply quality-based particle multiplier
    const particleMultiplier = window.qualitySettings?.get().particleMultiplier ?? 1;
    let count = Math.floor((this.BASE_PARTICLE_COUNT / 100) * intensity * particleMultiplier);
    
    if (count === 0) return;
    
    this.currentIntensity = intensity;

    const positions = new Float32Array(count * 3);
    const particleSizes = new Float32Array(count);
    const seeds = new Float32Array(count * 4); // x, y, z seed + speed multiplier

    for (let i = 0; i < count; i++) {
      // Initial position - poison particles stay lower to the ground
      positions[i * 3] = (Math.random() - 0.5) * (floorSize.x - 0.2) + 0.3;
      positions[i * 3 + 1] = this.startingLowerLimit + Math.random() * (this.upperLimit - this.startingLowerLimit);
      positions[i * 3 + 2] = (Math.random() - 0.5) * (floorSize.z - 0.2) - 0.3;
      
      // Particle size - poison particles are larger
      particleSizes[i] = Math.random() * this.PARTICLE_SIZE + 0.02;
      
      // Random seeds for GPU animation
      seeds[i * 4] = Math.random() * 6.28318; // phase X
      seeds[i * 4 + 1] = Math.random() * 6.28318; // phase Y
      seeds[i * 4 + 2] = Math.random() * 6.28318; // phase Z
      seeds[i * 4 + 3] = 0.2 + Math.random() * 0.6; // speed multiplier (slower than dust)
    }

    const particlesGeometry = new THREE.BufferGeometry();
    particlesGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    particlesGeometry.setAttribute("sizes", new THREE.BufferAttribute(particleSizes, 1));
    particlesGeometry.setAttribute("seed", new THREE.BufferAttribute(seeds, 4));

    // Store bounds for shader
    const bounds = {
      minY: this.startingLowerLimit,
      maxY: this.upperLimit,
      rangeY: this.upperLimit - this.startingLowerLimit,
    };

    let particlesMaterial = new THREE.PointsMaterial({
      transparent: true,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      onBeforeCompile: (shader) => {
        shader.uniforms.time = window.gu.time;
        shader.uniforms.boundsMinY = { value: bounds.minY };
        shader.uniforms.boundsRangeY = { value: bounds.rangeY };
        
        shader.vertexShader = `
          uniform float time;
          uniform float boundsMinY;
          uniform float boundsRangeY;
          
          attribute float sizes;
          attribute vec4 seed;
          
          varying vec3 vColor;

          uniform float size;
          uniform float scale;
          
          #include <common>
          #include <color_pars_vertex>
          #include <fog_pars_vertex>
          #include <morphtarget_pars_vertex>
          #include <logdepthbuf_pars_vertex>
          #include <clipping_planes_pars_vertex>
          
          #ifdef USE_POINTS_UV
            varying vec2 vUv;
            uniform mat3 uvTransform;
          #endif
          
          void main() {
            #ifdef USE_POINTS_UV
              vUv = (uvTransform * vec3(uv, 1)).xy;
            #endif
            
            #include <color_vertex>
            // Green color for poison/effluvium
            vColor = vec3(22., 222., 0.) / 255.;
            
            #include <morphinstance_vertex>
            #include <morphcolor_vertex>
            #include <begin_vertex>
            
            // GPU-based animation - ominous swirling movement for poison
            float t = time * 0.4 * seed.w;
            
            // Swirling, smoky motion with more pronounced movement
            float dx = sin(t * 0.7 + seed.x) * 0.25 + sin(t * 1.2 + seed.x * 1.5) * 0.12;
            float dy = sin(t * 0.5 + seed.y) * 0.08 + t * 0.03; // Upward drift
            float dz = sin(t * 0.9 + seed.z) * 0.20 + sin(t * 1.5 + seed.z * 1.5) * 0.10;
            
            transformed.x += dx;
            transformed.z += dz;
            
            // Wrap Y position for continuous floating
            float newY = transformed.y + dy;
            transformed.y = boundsMinY + mod(newY - boundsMinY, boundsRangeY);
            
            #include <morphtarget_vertex>
            #include <project_vertex>
            
            gl_PointSize = size * sizes;
            
            #ifdef USE_SIZEATTENUATION
              bool isPerspective = isPerspectiveMatrix(projectionMatrix);
              if (isPerspective) gl_PointSize *= (scale / -mvPosition.z);
            #endif
            
            #include <logdepthbuf_vertex>
            #include <clipping_planes_vertex>
            #include <worldpos_vertex>
            #include <fog_vertex>
          }
        `;
        
        shader.fragmentShader = `
          varying vec3 vColor;
          uniform vec3 diffuse;
          uniform float opacity;
          
          #include <common>
          #include <color_pars_fragment>
          #include <map_particle_pars_fragment>
          #include <alphatest_pars_fragment>
          #include <alphahash_pars_fragment>
          #include <fog_pars_fragment>
          #include <logdepthbuf_pars_fragment>
          #include <clipping_planes_pars_fragment>
          
          void main() {
            #include <clipping_planes_fragment>
            
            float d = length(gl_PointCoord.xy - 0.5);
            vec4 diffuseColor = vec4(vColor, smoothstep(0.5, 0.1, d) * 0.5);
            vec3 outgoingLight = vec3(0.0);
            
            #include <logdepthbuf_fragment>
            #include <map_particle_fragment>
            #include <color_fragment>
            #include <alphatest_fragment>
            #include <alphahash_fragment>
            
            outgoingLight = diffuseColor.rgb;
            
            #include <opaque_fragment>
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
            #include <fog_fragment>
            #include <premultiplied_alpha_fragment>
          }
        `;
      },
    });

    this.particleSystem = new THREE.Points(particlesGeometry, particlesMaterial);
    this.group.add(this.particleSystem);
    
    // No more CPU animation loop needed - GPU handles everything!
  }
}
